import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4,
  Brightness7,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout as logoutAction } from '../../redux/actions/authActions';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import InAppNotifications from '../InAppNotifications/InAppNotifications';
import NotificationServices from '../../services/NotificationServices';
import { resolveCurrentUserId } from '../../utils/authSessionStorage';

const Header = ({ onMenuClick, sidebarWidth = 240, setCollapsed }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { isDarkMode } = useSelector(state => state.theme);
  const user = useSelector(state => state.auth.user);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = React.useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const open = Boolean(anchorEl);
  const notificationOpen = Boolean(notificationAnchorEl);
  const navigate = useNavigate();

  // Fetch unread notification count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const currentUserId = resolveCurrentUserId(user);
        if (!currentUserId) {
          return;
        }

        const response = await NotificationServices.get({
          user_id: currentUserId,
          type: 'in_app',
          is_read: false,
        });

        if (response?.data?.notifications) {
          const totalUnread = response.data.notifications.totalItems || 0;
          setUnreadCount(totalUnread);
        }
      } catch (err) {
        console.error('Error fetching unread count:', err);
      }
    };

    fetchUnreadCount();
    
    // Refresh unread count every .5 minute
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const toggleTheme = () => {
    dispatch({ type: 'TOGGLE_THEME' });
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleNotificationClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
    setUnreadCount(0);
  };
  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };
  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };
  const handleLogout = async () => {
    handleMenuClose();
    await dispatch(logoutAction());
    sessionStorage.removeItem('organization_config');
    navigate('/login');
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        ml: { md: `${sidebarWidth}px` },
        width: { md: `calc(100% - ${sidebarWidth}px)` },
        transition: 'margin-left 0.3s, width 0.3s',
        top: 0,
        left: 0,
        zIndex: (theme) => theme.zIndex.drawer - 1,
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 48, md: 50 } }}>{/* top bar height adjust */}
        <IconButton
          //color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={() => {
            if (isMobile) {
              onMenuClick();
            } else {
              setCollapsed((prev) => !prev);
            }
          }}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>

        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton sx={{ mr: 2 }} onClick={handleNotificationClick}>
            <Badge 
              badgeContent={unreadCount} 
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.6rem',
                  height: 18,
                  minWidth: 18,
                  padding: '0 3px',
                }
              }}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <InAppNotifications 
            anchorEl={notificationAnchorEl}
            open={notificationOpen}
            onClose={handleNotificationClose}
          />
          <Divider orientation="vertical" flexItem />
          <Avatar  sx={{ width: 32, height: 32, marginLeft:'3px' }}>
            {user?.name ? user?.name[0].toUpperCase() : 'U'}
          </Avatar>
          <div className='px-2 mt--1'>
            <Typography variant='p'>
              {user?.name}
            </Typography>

          </div>

          <IconButton onClick={handleAvatarClick} sx={{ ml: 1 }}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleProfile}><div className='flex'><AccountCircleOutlinedIcon className='px-1' /> Profile</div></MenuItem>
            <Divider />
            <MenuItem onClick={toggleTheme}>{isDarkMode ? <div className='flex'><Brightness7 className='px-1' />  Light Mode</div> : <div className='flex'><Brightness4 className='px-1' />  Dark Mode </div>}</MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}><div className='flex'><LogoutOutlinedIcon className='px-1' /> Logout</div></MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;